<script setup lang="ts">
import { computed } from 'vue';
import { categoriesOfType } from '@/data/categories';
import { maskIcon } from '@/data/css';
import type { LocationType } from '@/data/types';
import buildingIcon from '@/assets/ui/icons/building-columns.svg';
import parkingIcon from '@/assets/ui/icons/circle-parking.svg';

const props = defineProps<{ counts: Map<string, number> }>();
const type = defineModel<LocationType>('type', { required: true });
const emit = defineEmits<{ open: [categoryKey: string] }>();

const tabs: { type: LocationType; label: string; icon: string }[] = [
  { type: 'building', label: 'Buildings', icon: buildingIcon },
  { type: 'parking', label: 'Parking', icon: parkingIcon },
];

// Hide categories nobody has entered locations for yet.
const visible = computed(() => categoriesOfType(type.value).filter((c) => (props.counts.get(c.key) ?? 0) > 0));

function onTabKeydown(e: KeyboardEvent) {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
  e.preventDefault();
  const next = type.value === 'building' ? 'parking' : 'building';
  type.value = next;
  document.getElementById(`tab-${next}`)?.focus();
}
</script>

<template>
  <div>
    <div class="tabs" role="tablist" aria-label="Location type">
      <button
        v-for="tab in tabs"
        :id="`tab-${tab.type}`"
        :key="tab.type"
        type="button"
        role="tab"
        class="tab"
        :class="{ 'is-selected': type === tab.type }"
        :aria-selected="type === tab.type"
        :tabindex="type === tab.type ? 0 : -1"
        aria-controls="category-panel"
        @click="type = tab.type"
        @keydown="onTabKeydown"
      >
        <span class="mask-icon" :style="maskIcon(tab.icon)" aria-hidden="true" />
        {{ tab.label }}
      </button>
    </div>

    <ul id="category-panel" class="categories" role="tabpanel" :aria-labelledby="`tab-${type}`">
      <li v-for="c in visible" :key="c.key">
        <button type="button" class="category" @click="emit('open', c.key)">
          <span class="mask-icon category__icon" :style="maskIcon(c.iconUrl)" aria-hidden="true" />
          <span class="category__label">{{ c.label }}</span>
          <span class="category__count" :aria-label="`${counts.get(c.key)} locations`">{{ counts.get(c.key) }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid var(--interactive-blue);
  border-radius: var(--radius);
  overflow: hidden;
}

.tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  border: 0;
  background: var(--surface);
  color: var(--interactive-blue);
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tab .mask-icon {
  width: 16px;
  height: 16px;
}

.tab.is-selected {
  background: var(--interactive-blue);
  color: #fff;
}

.categories {
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
}

.category {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 0 8px;
  border: 0;
  border-bottom: 1px solid var(--border);
  background: none;
  text-align: left;
  font-weight: 600;
  font-size: 15px;
}

li:last-child .category {
  border-bottom: 0;
}

.category:hover {
  background: var(--surface-alt);
}

.category__icon {
  color: var(--interactive-blue);
}

.category__label {
  flex: 1;
}

.category__count {
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--surface-alt);
  color: var(--text-muted);
  font-size: 13px;
  text-align: center;
}
</style>
